/**
 * @fileoverview User Management display helpers.
 * @description How the users table and the View/Edit User dialogs show a user's phone number,
 * joining date, full name and directory status.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { DirectoryStatus } from '@/features/users/types/users.types'

/** Each directory status's badge colour. */
export const DIRECTORY_STATUS_VARIANT: Record<DirectoryStatus, 'success' | 'warning' | 'neutral'> =
  {
    ACTIVE: 'success',
    PENDING: 'warning',
    INACTIVE: 'neutral',
  }

/** Each directory status's label (translation key). */
export const DIRECTORY_STATUS_LABEL_KEY: Record<DirectoryStatus, string> = {
  ACTIVE: 'sales.userManagement.filters.active',
  PENDING: 'sales.userManagement.filters.pendingInvitation',
  INACTIVE: 'sales.userManagement.filters.inactive',
}

/**
 * @description Formats a user's phone number with its calling code.
 * @param {{ countryCode: string | null; mobileNumber: string | null }} user - The user.
 * @returns {string} e.g. "+91 9876543210", or '—' when there's none.
 */
export function formatPhone(user: { countryCode: string | null; mobileNumber: string | null }) {
  if (!user.mobileNumber) {
    return '—'
  }
  return user.countryCode ? `${user.countryCode} ${user.mobileNumber}` : user.mobileNumber
}

/**
 * @description Formats a date of joining (YYYY-MM-DD) as "01 Apr 2025".
 * @param {string | null} date - The calendar date, or null before activation.
 * @returns {string} The formatted date, or '' when there is none.
 */
export function formatJoiningDate(date: string | null): string {
  if (!date) {
    return ''
  }
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * @description Joins a user's first, middle and last names.
 * @param {{ firstName: string; middleName: string | null; lastName: string }} user - The user.
 * @returns {string} The full name.
 */
export function fullName(user: { firstName: string; middleName: string | null; lastName: string }) {
  return [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' ')
}

/**
 * @description Builds a user's 2-letter initials from their name.
 * @param {string} name - The user's display name.
 * @returns {string} Up to two uppercase initials.
 */
export function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}
